import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../config/config.json';
import { toast } from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';
import ItemCard from '../../../components/ItemCard/ItemCard';
import { getRarityName } from '../../../utils/rarityUtils';

interface DeliveryRequest {
  id: number;
  game_name: string;
  game_password: string;
  realm: string;
  context: string;
  node_name: string;
  reseller_name: string;
  value: string;
  currency: string;
  remaining: string;
  completed: number;
  reroute_required: number;
  created_at: string;
  items?: any[];
}

interface DeliveryRequestListProps {
  requests: DeliveryRequest[];
}

export default function DeliveryRequestList({ requests }: DeliveryRequestListProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');
  const theme = useTheme();

  // State for expanded orders
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [selectedTradeItems, setSelectedTradeItems] = useState<{ [key: number]: any[] }>({});

  // Reroute modal state
  const [rerouteModalOpen, setRerouteModalOpen] = useState(false);
  const [selectedTradeForReroute, setSelectedTradeForReroute] = useState<DeliveryRequest | null>(
    null
  );
  const [rerouteNewGameName, setRerouteNewGameName] = useState('');
  const [rerouteNewGamePassword, setRerouteNewGamePassword] = useState('');
  const [rerouteNewGameRealm, setRerouteNewGameRealm] = useState('europe');

  const rerouteTradeMutation = useMutation({
    mutationFn: async ({
      tradeId,
      newGameName,
      newGamePassword,
      newGameRealm,
    }: {
      tradeId: number;
      newGameName: string;
      newGamePassword: string;
      newGameRealm: string;
    }) => {
      const response = await axios.post(
        `${config.backend_base_url}ml/trade/reroute`,
        {
          trade_id: tradeId,
          new_game_name: newGameName,
          new_game_password: newGamePassword,
          new_game_realm: newGameRealm,
          comment: '',
        },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryRequests'] });
      toast.success(
        'Trade has been rerouted, droppers will be killed automatically, please be ready in the new game!'
      );
      handleCloseRerouteModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error occurred.');
    },
  });

  const haltTradeMutation = useMutation({
    mutationFn: async (tradeId: number) => {
      const response = await axios.post(
        `${config.backend_base_url}v1/trade/halt`,
        { trade_id: tradeId },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryRequests'] });
      toast.success('Trade halted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error occurred while halting trade');
    },
  });

  const handleToggleExpanded = async (request: DeliveryRequest) => {
    const newExpandedOrders = new Set(expandedOrders);

    if (newExpandedOrders.has(request.id)) {
      newExpandedOrders.delete(request.id);
    } else {
      newExpandedOrders.add(request.id);

      // Fetch trade details if not already loaded
      if (!selectedTradeItems[request.id]) {
        try {
          const response = await axios.get(
            `${config.backend_base_url}rs/trade/detail/${request.id}`,
            { headers: { 'session-token': sessionToken } }
          );
          if (response.data && response.data.items) {
            setSelectedTradeItems((prev) => ({
              ...prev,
              [request.id]: response.data.items,
            }));
          }
        } catch (error) {
          console.error('Error fetching trade details:', error);
          toast.error('Error fetching trade details');
        }
      }
    }

    setExpandedOrders(newExpandedOrders);
  };

  const handleRerouteClick = (request: DeliveryRequest) => {
    setSelectedTradeForReroute(request);
    setRerouteNewGameName(request.game_name);
    setRerouteNewGamePassword(request.game_password);
    setRerouteNewGameRealm(request.realm);
    setRerouteModalOpen(true);
  };

  const handleHaltTradeClick = (request: DeliveryRequest) => {
    if (
      window.confirm(
        'Are you sure you want to stop this trade? Reroute will be required for it to continue.'
      )
    ) {
      haltTradeMutation.mutate(request.id);
    }
  };

  const handleRerouteSubmit = () => {
    if (
      selectedTradeForReroute &&
      rerouteNewGameName &&
      rerouteNewGamePassword &&
      rerouteNewGameRealm
    ) {
      rerouteTradeMutation.mutate({
        tradeId: selectedTradeForReroute.id,
        newGameName: rerouteNewGameName,
        newGamePassword: rerouteNewGamePassword,
        newGameRealm: rerouteNewGameRealm,
      });
    }
  };

  const handleCloseRerouteModal = () => {
    setRerouteModalOpen(false);
    setSelectedTradeForReroute(null);
    setRerouteNewGameName('');
    setRerouteNewGamePassword('');
    setRerouteNewGameRealm('europe');
  };

  if (!requests || requests.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1">{t('orders.no_delivery_requests')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
      {requests.map((request) => (
        <Box key={request.id}>
          <Box
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleToggleExpanded(request)}
                  sx={{ p: 0.5 }}
                >
                  {expandedOrders.has(request.id) ? <RemoveIcon /> : <AddIcon />}
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  #{request.id}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {request.reroute_required === 1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleRerouteClick(request)}
                  >
                    {t('orders.request_delivery')}
                  </Button>
                )}
                {request.completed === 0 && request.reroute_required === 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleHaltTradeClick(request)}
                    disabled={haltTradeMutation.isPending}
                  >
                    {t('orders.stop_this_trade')}
                  </Button>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Grid
              className={'TESTEN'}
              container
              spacing={2}
              justifyContent="center"
              sx={{ pb: '24px' }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('orders.game_name')}:
                    </Typography>
                    <Typography variant="body2">{request.game_name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('search.game.password')}:
                    </Typography>
                    <Typography variant="body2">{request.game_password}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('orders.realm')}:
                    </Typography>
                    <Typography variant="body2">{request.realm}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ textAlign: 'left' }}>
                  {/* Only show Remaining if not empty string */}
                  {request.remaining !== '' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', display: 'inline' }}
                      >
                        {t('orders.remaining')}:
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'inline', ml: 0.5 }}>
                        {request.remaining}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'inline' }}>
                      {t('orders.created_at')}:
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline', ml: 0.5 }}>
                      {new Date(request.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Expandable items section */}
          <Collapse in={expandedOrders.has(request.id)}>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ width: '100%' }}>
                <Grid container spacing={2}>
                  {selectedTradeItems[request.id]?.map((item: any) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                      <ItemCard
                        item={{
                          id: item.id,
                          name: item.name,
                          skin_image_path: item.skin_image_path,
                          image_path: item.image_path,
                          product_image_path: item.image_path,
                          stats_description:
                            item.stats_description && item.stats_description !== item.name
                              ? item.stats_description
                              : '',
                          original_rarity: item.rarity_id
                            ? getRarityName(item.rarity_id)
                            : item.rarity || 'Normal',
                          rarity_id: item.rarity_id
                            ? getRarityName(item.rarity_id)
                            : item.rarity || 'Normal',
                          fiat_price: item.fiat_price,
                          fg_price: item.fg_price,
                          d2rshopproductlink: '',
                        }}
                        sessionToken={sessionToken}
                        variant="default"
                        showPrice={true}
                        showContext={false}
                        showRarityIndicator={true}
                        actions={['track', 'share']}
                      />
                    </Grid>
                  ))}
                </Grid>
                {(!selectedTradeItems[request.id] ||
                  selectedTradeItems[request.id].length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No items found for this trade
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </Box>
      ))}

      {/* Reroute Modal */}
      <Dialog
        open={rerouteModalOpen}
        onClose={handleCloseRerouteModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          {t('orders.request_delivery_for_trade')} #{selectedTradeForReroute?.id}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('orders.new_game_name')}
                value={rerouteNewGameName}
                onChange={(e) => setRerouteNewGameName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('orders.new_game_password')}
                value={rerouteNewGamePassword}
                onChange={(e) => setRerouteNewGamePassword(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('orders.new_game_realm')}</InputLabel>
                <Select
                  value={rerouteNewGameRealm}
                  onChange={(e) => setRerouteNewGameRealm(e.target.value)}
                  label={t('orders.new_game_realm')}
                >
                  <MenuItem value="europe">{t('search.game.realm.europe')}</MenuItem>
                  <MenuItem value="americas">{t('search.game.realm.americas')}</MenuItem>
                  <MenuItem value="asia">{t('search.game.realm.asia')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.palette.background.paper, p: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseRerouteModal}
            disabled={rerouteTradeMutation.isPending}
            sx={{ mr: 1 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRerouteSubmit}
            disabled={
              !rerouteNewGameName ||
              !rerouteNewGamePassword ||
              !rerouteNewGameRealm ||
              rerouteTradeMutation.isPending
            }
          >
            {t('orders.request_delivery')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
